/* eslint-disable jsx-a11y/alt-text */
import React, {
    useRef, useCallback, useEffect, useLayoutEffect, useState
} from 'react';
import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import defaultStyles from './TextInput.module.scss';

const TextInput = ({
    icon,
    placeholder,
    onChange,
    value,
    styles,
    inputProps,
    outlineOnFocus,
    disabled,
    customValidation,
    getDisplayValue
}) => {
    const ref = useRef(null);

    useEffect(() => {
        ref.current.setCustomValidity(customValidation(value));
    }, [value, customValidation]);

    useLayoutEffect(() => {
        if (ref.current && inputProps.ref) {
            inputProps.ref(ref.current);
        }
    }, [inputProps]);

    const [isEditing, setIsEditing] = useState(false);

    const onChangeWrapped = useCallback((...args) => {
        const e = args[0];
        ref.current.setCustomValidity(customValidation(e.target.value));
        onChange(...args);
    }, [onChange, customValidation]);

    const onFocusCallback = useCallback(() => {
        if (typeof inputProps.onFocus === 'function') {
            inputProps.onFocus();
        }
        setIsEditing(true);
    },
    [inputProps, setIsEditing]);

    const onBlurCallback = useCallback((e) => {
        if (typeof inputProps.onBlur === 'function') {
            inputProps.onBlur(e);
        }
        setIsEditing(false);
    },
    [inputProps, setIsEditing]);

    return (
        <div className={ styles.container }>
            <input
                { ...inputProps }
                onChange={ onChangeWrapped }
                className={ classNames([inputProps.className], {
                    [styles.textInput]: true,
                    [styles.outlineOnFocus]: outlineOnFocus,
                    [styles.disabled]: disabled
                }) }
                onFocus={ onFocusCallback }
                onBlur={ onBlurCallback }
                value={ getDisplayValue(value, isEditing) }
                placeholder={ placeholder }
                disabled={ disabled }
                ref={ ref }
            />
            { icon && (<img className={ styles.textInputIcon } src={ icon } />) }
        </div>
    );
};

TextInput.propTypes = {
    icon: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    styles: PropTypes.objectOf(PropTypes.string),
    inputProps: PropTypes.shape({
        onBlur: PropTypes.func,
        onFocus: PropTypes.func,
        onChange: PropTypes.func,
        ref: PropTypes.func,
        className: PropTypes.string
    }),
    outlineOnFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    customValidation: PropTypes.func,
    getDisplayValue: PropTypes.func
};

TextInput.defaultProps = {
    icon: '',
    placeholder: '',
    value: '',
    styles: defaultStyles,
    inputProps: {},
    outlineOnFocus: true,
    disabled: false,
    customValidation: fp.constant(''),
    getDisplayValue: fp.identity
};

export default TextInput;
